/**
 * Core API client for the admin dashboard.
 *
 * All requests attach:
 *  - Authorization: Bearer <token> from localStorage
 *
 * Non-2xx responses throw `ApiError` with `status` and `message`.
 *
 * Money convention: all monetary values are integers in paise.
 */

// The fallback is b2berp's default API port for local development.
const API_BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:9390/api/v1";

const TOKEN_KEY = "admin_auth_token";

// ── Error type ────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  public readonly status: number | null;
  public readonly data?: unknown;

  constructor(message: string, status: number | null = null, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/** Shown when the server's permission check refuses a request. */
export const PERMISSION_DENIED_MESSAGE =
  "You don't have permission to do this. Ask the owner to add it to your role.";

// ── Storage helpers ───────────────────────────────────────────────────────────

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// ── Session end ───────────────────────────────────────────────────────────────

// Called when the server refuses a signed-in call with 401: the login is over
// because the password changed, the account was switched off, the role or
// outlets changed, or it expired (#159). The auth store registers the handler;
// it is here, not there, so this module does not import the store.
let sessionEndedHandler: (() => void) | null = null;

export function onSessionEnded(handler: (() => void) | null): void {
  sessionEndedHandler = handler;
}

// A wrong password on the sign-in form is a 401 too, and not a session ending.
function endsSession(path: string): boolean {
  return !path.startsWith("/auth/signin") && !path.startsWith("/auth/mfa/");
}

// ── Query string builder ──────────────────────────────────────────────────────

export function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return "";
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    qs.set(key, String(value));
  }
  const str = qs.toString();
  return str ? `?${str}` : "";
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };

  // Skip JSON content type if body is FormData
  if (!(init.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch (err) {
    throw new ApiError(
      "Network error — could not reach the server. Check your connection.",
      null
    );
  }

  if (response.status === 204) {
    return null as unknown as T;
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError(
      `Unexpected server response (HTTP ${response.status})`,
      response.status
    );
  }

  // Only if this request's token is still the stored one: a request sent
  // before a fresh sign-in must not end the new session when it fails.
  if (response.status === 401 && token && getToken() === token && endsSession(path)) {
    sessionEndedHandler?.();
  }

  if (!response.ok) {
    const err = body as { error?: string; message?: string };
    // The server's permission check refuses with {"error":"forbidden"}. Its
    // own wording is terse ("route not permitted" for a route it does not
    // know), so every screen gets one sentence that says what to do. Other
    // 403s (a removed device, an org-only page) keep their own message.
    const msg =
      response.status === 403 && err.error === "forbidden"
        ? PERMISSION_DENIED_MESSAGE
        : err.message ?? err.error ?? `Request failed (${response.status})`;
    throw new ApiError(msg, response.status, body);
  }

  return body as T;
}

// ── Public API surface ────────────────────────────────────────────────────────

/**
 * Read an error response's body for something worth showing.
 *
 * Without this a refusal reads as "Request failed (403)", which sends
 * somebody to look at their network rather than at their permissions.
 */
async function describeFailure(response: Response): Promise<string> {
  const fallback = `Request failed (${response.status})`;
  try {
    const body = (await response.json()) as { error?: string; message?: string };
    if (response.status === 403 && body.error === "forbidden") {
      return PERMISSION_DENIED_MESSAGE;
    }
    return body.message ?? body.error ?? fallback;
  } catch {
    return fallback;
  }
}

export const api = {
  get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    return apiFetch<T>(`${path}${buildQuery(params)}`);
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    const serialized = body !== undefined ? JSON.stringify(body) : undefined;
    console.log(`POST ${path} payload:`, serialized);
    return apiFetch<T>(path, {
      method: "POST",
      body: serialized,
    });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return apiFetch<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  postForm<T>(path: string, body: FormData): Promise<T> {
    return apiFetch<T>(path, {
      method: "POST",
      body,
    });
  },

  del(path: string, body?: unknown): Promise<void> {
    return apiFetch<void>(path, { 
      method: "DELETE",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  /** POST a body and read a text/html response as-is, authenticated. For a
   *  server-rendered preview of something not yet saved. */
  async postText(path: string, body?: unknown): Promise<string> {
    const token = getToken();
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new ApiError("Network error — could not reach the server. Check your connection.", null);
    }
    if (!response.ok) {
      throw new ApiError(`Request failed (${response.status})`, response.status);
    }
    return response.text();
  },

  /** Fetch a text/html response as-is, authenticated. For a server-rendered
   *  document that is shown or printed rather than parsed. */
  async text(path: string): Promise<string> {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}`, { headers });
    } catch {
      throw new ApiError("Network error — could not reach the server. Check your connection.", null);
    }
    if (!response.ok) {
      throw new ApiError(await describeFailure(response), response.status);
    }
    return response.text();
  },

  /** Fetch a binary file (an .xlsx, say) as a Blob, authenticated. `download`
   *  reads the body as text, which corrupts anything that is not. */
  async downloadBlob(path: string, fallbackName = "download"): Promise<{ blob: Blob; filename: string }> {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}`, { headers });
    } catch {
      throw new ApiError("Network error — could not reach the server. Check your connection.", null);
    }
    if (!response.ok) {
      throw new ApiError(await describeFailure(response), response.status);
    }
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = disposition.match(/filename[^;=\n]*=["']?([^"'\n;]+)["']?/);
    return { blob: await response.blob(), filename: match?.[1]?.trim() ?? fallbackName };
  },

  /** Fetch a file download — returns raw text content + filename from Content-Disposition */
  async download(path: string, params?: Record<string, unknown>): Promise<{ content: string; filename: string }> {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}${buildQuery(params)}`, { headers });
    } catch {
      throw new ApiError("Network error — could not reach the server. Check your connection.", null);
    }

    if (!response.ok) {
      let msg = `Request failed (${response.status})`;
      try {
        const body = await response.json() as { error?: string; message?: string };
        msg =
          response.status === 403 && body.error === "forbidden"
            ? PERMISSION_DENIED_MESSAGE
            : body.message ?? body.error ?? msg;
      } catch { /* ignore */ }
      throw new ApiError(msg, response.status);
    }

    const content = await response.text();
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const match = disposition.match(/filename[^;=\n]*=["']?([^"'\n;]+)["']?/);
    const filename = match?.[1]?.trim() ?? "download";
    return { content, filename };
  },
};
